import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { CreativeTypes } from '@bynder-studio/render-core';
import { Button, Dropdown, token } from '@bynder/design-system';
import { IconFilterFunnel } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';

const STORAGE_KEY_PREFIX = 'componentOverviewFilters';

function getStorageKey(isImageDesign: boolean): string {
    return `${STORAGE_KEY_PREFIX}:${isImageDesign ? 'image' : 'video'}`;
}

export type TypeFilters = {
    [CreativeTypes.IMAGE]: boolean;
    [CreativeTypes.VIDEO]: boolean;
};

export function getDefaultFilters(isImageDesign: boolean): TypeFilters {
    return {
        [CreativeTypes.IMAGE]: true,
        [CreativeTypes.VIDEO]: !isImageDesign,
    };
}

export function loadFilters(isImageDesign: boolean): TypeFilters {
    try {
        const stored = sessionStorage.getItem(getStorageKey(isImageDesign));

        if (stored) {
            const parsed = JSON.parse(stored);

            if (typeof parsed[CreativeTypes.IMAGE] === 'boolean' && typeof parsed[CreativeTypes.VIDEO] === 'boolean') {
                return parsed;
            }
        }
    } catch {
        // fall through to defaults
    }

    return getDefaultFilters(isImageDesign);
}

function saveFilters(isImageDesign: boolean, filters: TypeFilters) {
    try {
        sessionStorage.setItem(getStorageKey(isImageDesign), JSON.stringify(filters));
    } catch {
        // ignore storage errors
    }
}

export function getActiveTypes(filters: TypeFilters): CreativeTypes[] {
    return Object.entries(filters)
        .filter(([, enabled]) => enabled)
        .map(([type]) => type as CreativeTypes);
}

interface Props {
    filters: TypeFilters;
    onChange: (filters: TypeFilters) => void;
    isImageDesign: boolean;
}

const FilterButtonWrapper = styled.div`
    position: relative;
    display: inline-flex;
`;

const ActiveDot = styled.div`
    position: absolute;
    bottom: 4px;
    right: 4px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${token.green500};
    pointer-events: none;
`;

export default function OverviewFilters({ filters, onChange, isImageDesign }: Props) {
    const { translate } = useTranslate();

    const isCustom = useMemo(() => {
        const defaults = getDefaultFilters(isImageDesign);

        return (
            filters[CreativeTypes.IMAGE] !== defaults[CreativeTypes.IMAGE] ||
            filters[CreativeTypes.VIDEO] !== defaults[CreativeTypes.VIDEO]
        );
    }, [filters, isImageDesign]);

    const toggle = useCallback(
        (type: CreativeTypes) => {
            const updated = { ...filters, [type]: !filters[type] };

            // Prevent deselecting both — keep at least one selected
            if (!updated[CreativeTypes.IMAGE] && !updated[CreativeTypes.VIDEO]) {
                return;
            }

            saveFilters(isImageDesign, updated);
            onChange(updated);
        },
        [filters, onChange, isImageDesign],
    );

    return (
        <Dropdown
            trigger={({ isOpen, ...triggerProps }) => (
                <FilterButtonWrapper>
                    <Button
                        variant="clean"
                        isPressed={isOpen}
                        icon={<IconFilterFunnel />}
                        aria-label={translate('components.overview.filters.button.label')}
                        {...triggerProps}
                    />
                    {isCustom && <ActiveDot />}
                </FilterButtonWrapper>
            )}
        >
            <Dropdown.SectionTitle>{translate('components.overview.filters.show')}</Dropdown.SectionTitle>
            <Dropdown.Item
                checkedVariant="checkbox"
                isChecked={filters[CreativeTypes.IMAGE]}
                onClick={() => toggle(CreativeTypes.IMAGE)}
            >
                {translate('components.overview.filters.imageComponents')}
            </Dropdown.Item>
            <Dropdown.Item
                checkedVariant="checkbox"
                isChecked={filters[CreativeTypes.VIDEO]}
                onClick={() => toggle(CreativeTypes.VIDEO)}
            >
                {translate('components.overview.filters.videoComponents')}
            </Dropdown.Item>
        </Dropdown>
    );
}
